"use client";

import { useState } from "react";
import { LoaderCircle, Sparkles, Wand2 } from "lucide-react";
import { toast } from "sonner";

import EasterWallpaperGrid from "@/components/easter/wallpaper-grid";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { easterGeneratorPrompts } from "@/content/easter-wallpaper-site";

type GeneratedWallpaper = {
  id: string;
  src: string;
  alt: string;
  note: string;
  downloadName: string;
};

type WallpaperApiItem = {
  uuid?: string;
  img_url?: string | null;
};

export default function EasterWallpaperGenerator() {
  const [prompt, setPrompt] = useState(easterGeneratorPrompts[0]);
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState<GeneratedWallpaper[]>([]);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error("Enter an Easter wallpaper idea first.");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch("/api/gen-wallpaper", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ description: prompt.trim() }),
      });

      if (!response.ok) {
        throw new Error("Generation failed");
      }

      const payload = await response.json();
      const wallpapers = (payload?.data?.wallpapers ||
        payload?.wallpapers ||
        []) as WallpaperApiItem[];

      if (!wallpapers.length) {
        throw new Error("No wallpapers returned");
      }

      const normalized = wallpapers
        .filter((item) => item.img_url)
        .map((item, index) => ({
          id: item.uuid || `${index}`,
          src: item.img_url as string,
          alt: `Generated Easter wallpaper ${index + 1}`,
          note: prompt.trim(),
          downloadName: `generated-easter-wallpaper-${index + 1}.png`,
        }));

      setGenerated(normalized);
      toast.success("Your Easter wallpaper is ready.");
    } catch (error) {
      console.error(error);
      toast.error("Could not generate the wallpaper right now.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="generate"
      className="py-20"
    >
      <div className="container">
        <div className="overflow-hidden rounded-[40px] border border-[#d7c8bc] bg-[linear-gradient(145deg,#fffaf5_0%,#f6ecdf_55%,#f3e7d9_100%)] shadow-[0_40px_120px_rgba(84,64,45,0.08)]">
          <div className="grid gap-10 px-6 py-8 lg:grid-cols-[0.95fr_1.05fr] lg:px-10 lg:py-12">
            <div className="rounded-[30px] border border-[#e3d6cb] bg-white/72 p-6">
              <div className="inline-flex items-center gap-2 rounded-full border border-[#ead9c8] bg-[#fff6ec] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.28em] text-[#9a6c47]">
                <Wand2 className="h-3.5 w-3.5" />
                Easter Generator
              </div>
              <h2 className="mt-5 text-3xl font-semibold tracking-[-0.03em] text-[#2e3529] sm:text-4xl">
                Make a custom Easter wallpaper from one prompt
              </h2>
              <p className="mt-4 text-base leading-7 text-[#5b6256]">
                The generator stays Easter-focused on purpose. Describe the mood,
                palette, or symbolism you want, and the prompt is wrapped with
                spring wallpaper art direction before generation.
              </p>

              <div className="mt-6 flex flex-wrap gap-3">
                {easterGeneratorPrompts.map((idea) => (
                  <button
                    key={idea}
                    type="button"
                    onClick={() => setPrompt(idea)}
                    className="rounded-full border border-[#ddcec0] bg-[#fffaf4] px-4 py-2 text-sm text-[#53624f] transition hover:border-[#c7b29f] hover:text-[#2f5c47]"
                  >
                    {idea}
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-[30px] border border-[#e3d6cb] bg-white/78 p-6">
              <label
                htmlFor="easter-generator-prompt"
                className="text-sm font-semibold uppercase tracking-[0.24em] text-[#8f6b4f]"
              >
                Prompt
              </label>
              <Textarea
                id="easter-generator-prompt"
                value={prompt}
                onChange={(event) => setPrompt(event.target.value)}
                placeholder="Describe the Easter wallpaper you want..."
                className="mt-3 min-h-[180px] rounded-[24px] border-[#ddcec0] bg-[#fffaf5] px-5 py-4 text-base text-[#364033] shadow-none focus-visible:ring-[#cfaf8b]"
              />
              <div className="mt-5 flex items-center justify-between gap-4">
                <p className="text-sm leading-6 text-[#6a7265]">
                  Good prompts mention mood, subject, and color. Example:
                  “quiet sunrise cross with lilies and warm gold light.”
                </p>
                <Button
                  type="button"
                  onClick={handleGenerate}
                  disabled={loading}
                  className="h-12 rounded-full bg-[#2f5c47] px-6 text-sm font-semibold text-white hover:bg-[#284d3c]"
                >
                  {loading ? (
                    <>
                      <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                      Generating
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" />
                      Generate
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {generated.length ? (
          <EasterWallpaperGrid
            eyebrow="Generated"
            title="Fresh Easter wallpaper results"
            description="Preview the latest generated variation and download it immediately."
            wallpapers={generated}
          />
        ) : null}
      </div>
    </section>
  );
}
